import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import io from 'socket.io-client';
import { API_BASE_URL, SOCKET_URL } from '../utils/network.js';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);
  

  // Initialize socket with useMemo to prevent unnecessary reconnections
  const initializeSocket = useMemo(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!token || !userId) {
      console.log('No token or userId found, skipping socket connection');
      return null;
    }

    console.log('🔌 Initializing socket connection...');
    
    return io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: false // Don't connect automatically, we'll connect manually
    });
  }, []);

  useEffect(() => {
    if (!initializeSocket) {
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current) {
      console.log('Disconnecting existing socket...');
      socketRef.current.disconnect();
    }

    socketRef.current = initializeSocket;
    const currentSocket = socketRef.current;

    // Setup event listeners
    currentSocket.on('connect', () => {
      console.log('✅ Socket connected successfully');
      setIsConnected(true);
      setSocket(currentSocket);
      
      // Join user's personal room
      const userId = localStorage.getItem('userId');
      if (userId) {
        currentSocket.emit('join-user-room', userId);
        currentSocket.emit('user-online', userId);
        console.log(`✅ Joined user room: user-${userId}`);
      }
    });

    currentSocket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setIsConnected(false);
      setSocket(null);
      
      // Try to reconnect if not manually disconnected
      if (reason === 'io server disconnect') {
        console.log('🔄 Server disconnected, attempting to reconnect...');
        setTimeout(() => {
          if (currentSocket) {
            currentSocket.connect();
          }
        }, 2000);
      }
    });

    currentSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      setIsConnected(false);
    });

    currentSocket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
      setIsConnected(true);
      // Re-join user's room after reconnection
      const userId = localStorage.getItem('userId');
      if (userId) {
        currentSocket.emit('join-user-room', userId);
        currentSocket.emit('user-online', userId);
      }
    });

    currentSocket.on('reconnect_error', (error) => {
      console.error('❌ Socket reconnection error:', error);
    });

    currentSocket.on('reconnect_failed', () => {
      console.error('❌ Socket reconnection failed');
    });

    // ================= NOTIFICATION EVENTS =================
    currentSocket.on('new-notification', (notification) => {
      console.log('📨 New notification:', notification);
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    });

    currentSocket.on('notification-read', ({ notificationId }) => {
      console.log('📨 Notification marked as read:', notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    currentSocket.on('all-notifications-read', () => {
      console.log('📨 All notifications marked as read');
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      setUnreadCount(0);
    });

    // ================= FOLLOW REQUEST EVENTS =================
    currentSocket.on('new-follow-request', (request) => {
      console.log('👥 New follow request:', request);
      
      const notification = {
        _id: Date.now().toString(),
        type: 'follow_request',
        sender: request.fromUser,
        requestId: request.requestId,
        createdAt: request.createdAt,
        isRead: false
      };
      
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    });

    currentSocket.on('follow-request-accepted', (data) => {
      console.log('✅ Follow request accepted:', data);
    });

    currentSocket.on('follow-request-updated', (data) => {
      console.log('🔄 Follow request updated:', data);
    });

    currentSocket.on('follow-request-cancelled', (data) => {
      console.log('❌ Follow request cancelled:', data);
    });

    // ================= MESSAGING EVENTS =================
    currentSocket.on('user-typing', (data) => {
      console.log('⌨️ User typing:', data);
    });

    currentSocket.on('user-status-changed', (data) => {
      console.log('📱 User status changed:', data);
    });

    currentSocket.on('new-message', (message) => {
      console.log('💬 New message:', message);
    });

    // ================= POST EVENTS =================
    currentSocket.on('new-post', (postData) => {
      console.log('📝 New post:', postData);
    });

    currentSocket.on('post-liked', (data) => {
      console.log('❤️ Post liked:', data);
    });

    currentSocket.on('post-commented', (data) => {
      console.log('💬 Post commented:', data);
    });

    // Now connect the socket
    console.log('🔌 Connecting socket...');
    currentSocket.connect();

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up socket connection');
      if (currentSocket && currentSocket.connected) {
        currentSocket.disconnect();
      }
      setIsConnected(false);
      setSocket(null);
    };
  }, [initializeSocket]);

  // Effect to handle token changes
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (token && userId && initializeSocket && !socketRef.current?.connected) {
        console.log('🔄 Token/userId changed, reconnecting socket...');
        socketRef.current?.connect();
      } else if (!token && socketRef.current?.connected) {
        console.log('🔒 Token removed, disconnecting socket...');
        socketRef.current.disconnect();
        setIsConnected(false);
        setSocket(null);
      }
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also check on component mount
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (token && userId && initializeSocket && !socketRef.current?.connected) {
      console.log('🔌 Component mounted with token, connecting socket...');
      socketRef.current?.connect();
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [initializeSocket]);

  // Safe emit function that checks connection
  const safeEmit = (event, data, fallback = null) => {
    if (socketRef.current && socketRef.current.connected) {
      console.log(`📤 Emitting ${event}:`, data);
      socketRef.current.emit(event, data);
      return true;
    } else {
      console.warn(`⚠️ Cannot emit ${event}: Socket not connected`);
      if (typeof fallback === 'function') {
        console.log(`🔄 Socket down, running fallback for ${event}`);
        fallback();
      }
      return false;
    }
  };

  const contextValue = {
    socket,
    isConnected,
    notifications,
    unreadCount,
    setNotifications,
    setUnreadCount,

    // Helper functions
    emit: safeEmit,
    
    on: (event, callback) => {
      if (socketRef.current) {
        console.log(`👂 Subscribing to ${event}`);
        socketRef.current.on(event, callback);
      }
      return () => {
        if (socketRef.current) {
          socketRef.current.off(event, callback);
        }
      };
    },
    
    off: (event, callback) => {
      if (socketRef.current) {
        console.log(`❌ Unsubscribing from ${event}`);
        socketRef.current.off(event, callback);
      }
    },
    
    // Join conversation
    joinConversation: (conversationId) => {
      return safeEmit('join-conversation', conversationId);
    },
    
    // Leave conversation
    leaveConversation: (conversationId) => {
      return safeEmit('leave-conversation', conversationId);
    },
    
    // Typing indicator
    sendTyping: (conversationId, isTyping) => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        return safeEmit('typing', {
          conversationId,
          userId,
          isTyping
        });
      }
      return false;
    },
    
    // Mark notification as read
    markNotificationAsRead: (notificationId) => {
      return safeEmit('mark-notification-read', { notificationId });
    },
    
    // Send follow request
    sendFollowRequest: (toUserId, message) => {
      return safeEmit('send-follow-request', {
        toUserId,
        message
      });
    },
    
    // Send user online status
    sendUserOnline: () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        return safeEmit('user-online', userId);
      }
      return false;
    },
    
    disconnect: () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        setIsConnected(false);
        setSocket(null);
      }
    },
    
    reconnect: () => {
      if (socketRef.current) {
        socketRef.current.connect();
      }
    },
    
    // Check if socket is connected
    isSocketConnected: () => {
      return socketRef.current?.connected || false;
    }
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Export a simple hook for notifications
export const useNotifications = () => {
  const { notifications, unreadCount, setNotifications, setUnreadCount, markNotificationAsRead } = useSocket();
  
  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        markNotificationAsRead(notificationId);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const filterByType = (type) => {
    return notifications.filter(notif => notif.type === type);
  };

  const getCommunityNotifications = () => {
    const communityTypes = ['group_message', 'join_request', 'join_request_response', 'removed_from_group', 'promoted_to_admin'];
    return notifications.filter(notif => communityTypes.includes(notif.type));
  };

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    filterByType,
    getCommunityNotifications,
    refreshNotifications: () => {
      // This function can be implemented to fetch notifications from API
    }
  };
};

// Export a simple hook for follow requests
export const useFollowRequests = () => {
  const { emit } = useSocket();
  
  const sendFollowRequest = (toUserId, message) => {
    emit('send-follow-request', { toUserId, message });
  };
  
  const respondToFollowRequest = (requestId, action) => {
    emit('respond-follow-request', { requestId, action });
  };
  
  return {
    sendFollowRequest,
    respondToFollowRequest
  };
};

// Export default
export default SocketContext;
